import { useState, useEffect, useMemo } from 'react'
import notDeleted from '../../../helpers/notDeleted'
import { db } from '../../../firebase/config'
import { client } from '../../../hooks/Client'
import { useFirestore, useFirestoreOrderBy, useFirestoreGeneral, useFirestoreGeneralArrayContains } from '../../../firebase/useFirestore'
import DataType from '../../graphData/DataType'
import useEffectiveResearchIds from '../../../hooks/research/useEffectiveResearchIds'
import effectsWithPersonaResults, { indicatorsForEffect } from '../../../helpers/personas/effectsWithPersonaResults'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

const PersonaEffectIndicator = ({ indicator, researchIds, personaId }) => (
  <div className="home-kpi-chart-container">
    <div className="home-kpi-description-container">
      <div className="home-kpi-question-title-container">
        <p>{indicator.Question}</p>
      </div>
    </div>
    <DataType
      type={indicator.Type}
      fieldId={indicator.ID}
      researchId={researchIds}
      personaId={personaId}
    />
  </div>
)

export const PersonaEffectCard = ({ effect, researchIds, personaId }) => {
  const indicators = useFirestoreGeneralArrayContains(
    'QuestionnaireFields',
    'EffectId',
    effect.ID || ''
  ) || []

  const [hasResults, setHasResults] = useState(null) // null = loading

  useEffect(() => {
    if (!personaId || indicators.length === 0) {
      setHasResults(false)
      return
    }

    const fieldIds = indicators.map(i => i.ID).filter(Boolean)
    if (fieldIds.length === 0) {
      setHasResults(false)
      return
    }

    // Check if any response exists for this persona + these field IDs
    // Firestore 'in' limit is 10; check first chunk — enough to confirm existence
    const firstChunk = fieldIds.slice(0, 10)

    // Geen .limit(1): deze read gaat buiten de hooks om, dus verwijderde
    // antwoorden worden hier niet gefilterd. Met een limiet van 1 kan dat ene
    // document juist het verwijderde zijn, en dan claimt de kaart resultaten
    // die er niet meer zijn. Een kleine marge en daarna zelf filteren.
    // AI-CONTEXT: See docs/decisions.md (ADR-008)
    db.collection('QuestionnairesResponses')
      .where('CompagnyID', '==', client)
      .where('Persona', '==', personaId)
      .where('FieldID', 'in', firstChunk)
      .limit(20)
      .get()
      .then(snap => setHasResults(snap.docs.some(doc => notDeleted(doc.data()))))
      .catch(() => setHasResults(false))
  }, [personaId, indicators])

  if (hasResults === null || !hasResults) return null

  return (
    <div className="new-dashboard-card">
      <div className="new-dashboard-card-content">
        <h3 className="new-dashboard-card-title">{effect.Effect}</h3>
        {indicators.map(indicator => (
          <PersonaEffectIndicator
            key={indicator.ID}
            indicator={indicator}
            researchIds={researchIds}
            personaId={personaId}
          />
        ))}
      </div>
    </div>
  )
}

const PersonaEffects = ({ item }) => {
  // Handmatig gekoppeld plus elk onderzoek dat voor alle medewerkers geldt.
  // De hook houdt de array-identiteit stabiel; DataType zet hem in zijn deps.
  // AI-CONTEXT: See docs/features/automatische-deelname-onderzoek.md
  const { ids: researchIds } = useEffectiveResearchIds({ kind: 'persona', linkedIds: item.Research })
  const personaId = item.ID

  const [index, setIndex] = useState(0)

  const effects = useFirestoreOrderBy('OutputEffects', 'Position', 'asc')
  const fields = useFirestore('QuestionnaireFields')
  const responses = useFirestoreGeneral('QuestionnairesResponses', 'Persona', personaId ? personaId : '')

  const visibleEffects = useMemo(
    () => effectsWithPersonaResults(effects, fields, responses),
    [effects, fields, responses]
  )

  if (visibleEffects.length === 0) return null

  const safeIndex = Math.min(index, visibleEffects.length - 1)
  const currentEffect = visibleEffects[safeIndex]
  const indicators = indicatorsForEffect(fields, currentEffect.ID)

  const previousHandler = () => setIndex((safeIndex - 1 + visibleEffects.length) % visibleEffects.length)
  const nextHandler = () => setIndex((safeIndex + 1) % visibleEffects.length)

  return (
    <div className="new-dashboard-card">
      <div className="new-dashboard-card-content persona-effects-card-content">
        <div className="persona-effects-card-header">
          <h3 className="new-dashboard-card-title">Effecten</h3>
          {visibleEffects.length > 1 && (
            <div className="persona-effects-card-navigator">
              <KeyboardArrowLeftIcon onClick={previousHandler} />
              <p>{safeIndex + 1}/{visibleEffects.length}</p>
              <KeyboardArrowRightIcon onClick={nextHandler} />
            </div>
          )}
        </div>
        <h4 className="persona-effects-card-effect-title">{currentEffect.Effect}</h4>
        {indicators.map(indicator => (
          <PersonaEffectIndicator
            key={indicator.ID}
            indicator={indicator}
            researchIds={researchIds}
            personaId={personaId}
          />
        ))}
      </div>
    </div>
  )
}

export default PersonaEffects
